"use strict";
// packages/shared-schemas/src/leadRoutingMatchV2.ts
//
// Routing v2: pick the rotation rule for a lead using the four classification
// dimensions as well as service category, office and the legacy dealType.
// Pure. Read by pickAssignee only when featureFlags.routing_v2 is observe
// (computed and compared, v1 still decides) or enforce (v2 decides).
//
// Matching. A rule field constrains the match when it holds a value (a
// non-empty string, or a non-empty array). Every constrained field must
// match the lead:
//   serviceCategory, officeId   lead value equals the rule value
//   loanPurpose, propertyUse,   lead value is one of the rule values
//   loanStructure, loanAmount
//   borrowerSituation           lead situations intersect the rule values
//   dealType (legacy)           lead dealType equals it, exactly as v1.
//                               Never matched through the classification:
//                               a classified lead's dealType is its first
//                               legacy mapping only, so a classification
//                               fallback would let a legacy rule match where
//                               v1 does not (e.g. an upgrader investor
//                               against a dealType 'investment' rule) and
//                               break v1 parity. Rules that want the richer
//                               match use the classification dimensions.
//
// Score (spec §2.5): 8 serviceCategory + 4 officeId + 2 per classification
// dimension constrained + 1 legacy dealType.
//
// Winner. Rules are first placed in the v1 cascade tier their constraints
// belong to (any classification dimension counts as the "dealType" slot) and
// the tier order is the tenant's cascadePriority, exactly as v1 walks it. The
// highest score wins inside a tier; ties keep rule array order. This is what
// makes v2 a strict extension of v1: for rules that only set
// serviceCategory / dealType / officeId every rule in a tier has the same
// score, so v2 returns the rule v1 returns, for every lead and every
// cascadePriority, whatever classification the lead carries (golden test:
// __tests__/leadRoutingMatchV2.test.ts).
// Ranking on score alone would not: 8+4 (service+office) outranks 8+1
// (service+dealType), the reverse of v1's default order. This is the
// authoritative reading of §2.5 "highest score wins": the spec's own MUST
// (a legacy rule produces the same winner as v1 for every lead) cannot hold
// under a pure score ranking, so score ranks within a v1 tier only.
//
// A rule that constrains classification without a service category is never
// matched, as in v1 (there is no dealType-only tier): classification options
// are defined per service category, so such a rule has no defined meaning.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.5.
Object.defineProperty(exports, "__esModule", { value: true });
exports.RotationRuleV2MatchSchema = exports.SCORE_WEIGHTS = exports.RotationRuleV2Schema = exports.DEFAULT_CASCADE_PRIORITY = void 0;
exports.evaluateRotationRuleV2 = evaluateRotationRuleV2;
exports.matchLeadRotationV2 = matchLeadRotationV2;
exports.isLegacyRotationRule = isLegacyRotationRule;
exports.legacyRotationRulesOnly = legacyRotationRulesOnly;
const leadClassification_1 = require("./leadClassification");
const leadTaxonomy_1 = require("./leadTaxonomy");
Object.defineProperty(exports, "DEFAULT_CASCADE_PRIORITY", { enumerable: true, get: function () { return leadTaxonomy_1.DEFAULT_CASCADE_PRIORITY; } });
/**
 * A v2 rule is the stored rotation rule: RotationRuleSchema's match carries
 * the four optional classification arrays alongside the legacy fields, so
 * one routing-rules document serves both matchers.
 */
exports.RotationRuleV2Schema = leadTaxonomy_1.RotationRuleSchema;
exports.SCORE_WEIGHTS = Object.freeze({
    serviceCategory: 8,
    officeId: 4,
    dimension: 2,
    dealType: 1,
});
const DIMENSION_LEAD_FIELD = {
    loanPurpose: 'loanPurpose',
    propertyUse: 'propertyUse',
    borrowerSituation: 'borrowerSituations',
    loanStructure: 'loanStructure',
    loanAmount: 'loanAmount',
};
function hasValue(v) {
    return typeof v === 'string' && v !== '';
}
function constrainedValues(v) {
    return Array.isArray(v) && v.length > 0 ? v : null;
}
/**
 * Evaluate one rule against a lead. Returns null when any constrained field
 * misses; otherwise the rule's v1 tier and its score.
 */
function evaluateRotationRuleV2(lead, rule) {
    const m = rule.match ?? {};
    let score = 0;
    const service = hasValue(m.serviceCategory);
    if (service) {
        if (!hasValue(lead.serviceCategory) || lead.serviceCategory !== m.serviceCategory)
            return null;
        score += exports.SCORE_WEIGHTS.serviceCategory;
    }
    const office = hasValue(m.officeId);
    if (office) {
        if (!hasValue(lead.officeId) || lead.officeId !== m.officeId)
            return null;
        score += exports.SCORE_WEIGHTS.officeId;
    }
    let classified = false;
    const c = lead.classification ?? null;
    for (const dim of leadClassification_1.CLASSIFICATION_DIMENSIONS) {
        const wanted = constrainedValues(m[dim]);
        if (!wanted)
            continue;
        classified = true;
        const field = DIMENSION_LEAD_FIELD[dim];
        if (field === 'borrowerSituations') {
            const have = c?.borrowerSituations ?? [];
            if (!have.some((v) => wanted.includes(v)))
                return null;
        }
        else {
            const have = c?.[field] ?? null;
            if (!hasValue(have) || !wanted.includes(have))
                return null;
        }
        score += exports.SCORE_WEIGHTS.dimension;
    }
    if (hasValue(m.dealType)) {
        classified = true;
        // Exact equality only, as v1 (see the header: no classification fallback).
        if (!hasValue(lead.dealType) || lead.dealType !== m.dealType)
            return null;
        score += exports.SCORE_WEIGHTS.dealType;
    }
    let tier;
    if (service) {
        if (classified)
            tier = office ? 'service+dealType+office' : 'service+dealType';
        else
            tier = office ? 'service+office' : 'service';
    }
    else {
        if (classified)
            return null; // no tier for classification without a service category (v1 parity)
        tier = office ? 'office' : 'company';
    }
    return { tier, score };
}
/**
 * The rotation rule for a lead, with its score, or null when nothing matches.
 * Accepts the routing-rules document (honouring its cascadePriority, as v1
 * does) or a bare rule array (default cascade).
 */
function matchLeadRotationV2(lead, rules) {
    const rotations = isRuleArray(rules) ? rules : rules.rotations ?? [];
    const priority = (isRuleArray(rules) ? null : rules.cascadePriority) ?? leadTaxonomy_1.DEFAULT_CASCADE_PRIORITY;
    let best = null;
    for (const rule of rotations) {
        const hit = evaluateRotationRuleV2(lead, rule);
        if (!hit)
            continue;
        const rank = priority.indexOf(hit.tier);
        if (rank === -1)
            continue; // v1 never reaches a tier the tenant left out
        if (best === null ||
            rank < best.rank ||
            (rank === best.rank && hit.score > best.score)) {
            best = { rule, score: hit.score, rank };
        }
    }
    return best ? { rule: best.rule, score: best.score } : null;
}
function isRuleArray(rules) {
    return Array.isArray(rules);
}
/** True when a rule only uses the v1 fields (serviceCategory / dealType / officeId). */
function isLegacyRotationRule(rule) {
    const m = rule.match ?? {};
    return leadClassification_1.CLASSIFICATION_DIMENSIONS.every((d) => constrainedValues(m[d]) === null);
}
/**
 * The rules v1 may see. v1 matchLeadRotation ignores the classification
 * arrays, so a rule such as {serviceCategory:'finance', loanPurpose:['refinance']}
 * would match every finance lead at the plain 'service' tier. Callers that run
 * v1 (routing_v2 off or observe) pass the rules through this first so a rule
 * with classification dimensions only ever takes effect under enforce.
 * Keeps the routing-rules document shape (cascadePriority untouched).
 */
function legacyRotationRulesOnly(rules) {
    return { ...rules, rotations: rules.rotations.filter(isLegacyRotationRule) };
}
/** Zod helper for callers that validate a bare match object (routing preview). */
exports.RotationRuleV2MatchSchema = leadTaxonomy_1.RotationRuleSchema.shape.match;
